/**
 * KESS Payment Link Hook
 * Custom hook for managing KESS payment link operations
 */

import {useState, useCallback} from 'react';
import API from '../network/dashboardAPI';

export const useKessPaymentLink = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kessPaymentData, setKessPaymentData] = useState(null);
	const [paymentMethods, setPaymentMethods] = useState([]);

  /**
   * Query KESS payment by out_trade_no
   * @param {string} outTradeNo
   * @returns {Promise<Object>}
   */
  const queryKessPayment = async (outTradeNo) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await API.queryKessPaymentByTradeNo({ out_trade_no: outTradeNo })
        .then(res => res.json());

      if (response.success) {
        setKessPaymentData(response.data);
        return {
          success: true,
          data: response.data,
          message: response.message,
        };
      } else {
        setKessPaymentData(null);  // Clear old data on failure
        setError(response.message);
        return {
          success: false,
          message: response.message,
          code: response.code,
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to query KESS payment. Please try again.';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Link existing KESS payment to order
   * @param {number} orderId
   * @param {string} outTradeNo
   * @param {string} paymentReference
   * @param {number} amount
   * @returns {Promise<Object>}
   */
  const linkExistingPayment = async (orderId, outTradeNo, paymentReference, amount) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await API.linkExistingKessPayment({
        order_id: orderId,
        out_trade_no: outTradeNo,
        payment_reference: paymentReference,
        amount: amount,
      }).then(res => res.json());

      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message,
        };
      } else {
        setError(response.message);
        return {
          success: false,
          message: response.message,
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to link payment. Please try again.';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

	/**
	 * Manually link KESS payment to order (without KESS API verification)
	 * @param {number} orderId
	 * @param {string} outTradeNo
	 * @param {Object} paymentDetails - { amount, payment_reference?, payment_option?, p_token? }
	 * @returns {Promise<Object>}
	 */
	const linkManualPayment = async (orderId, outTradeNo, paymentDetails) => {
		setLoading(true);
		setError(null);

		try {
			const response = await API.linkManualKessPayment({
				order_id: orderId,
				out_trade_no: outTradeNo,
				amount: paymentDetails.amount,
				payment_reference: paymentDetails.payment_reference,
				payment_option: paymentDetails.payment_option,
				p_token: paymentDetails.p_token,
			}).then(res => res.json());

			if (response.success) {
				return {
					success: true,
					data: response.data,
					message: response.message,
				};
			} else {
				setError(response.message);
				return {
					success: false,
					message: response.message,
				};
			}
		} catch (err) {
			const errorMessage = err.message || 'Failed to manually link payment. Please try again.';
			setError(errorMessage);
			return {
				success: false,
				message: errorMessage,
			};
		} finally {
			setLoading(false);
		}
	};

  /**
   * Update generated payment link
   * @param {number} id
   * @param {Object} updates - { payment_reference?, p_txnld? }
   * @returns {Promise<Object>}
   */
  const updatePaymentLink = async (id, updates) => {
    setLoading(true);
    setError(null);

	  try {
      const response = await API.updateKessPaymentLink(id, updates)
        .then(res => res.json());

      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message,
        };
      } else {
        setError(response.message);
        return {
          success: false,
          message: response.message,
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to update payment link. Please try again.';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

	/**
	 * Fetch available payment methods from KESS
	 * @returns {Promise<Object>}
	 */
	const fetchPaymentMethods = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await API.getKessPaymentMethods()
				.then(res => res.json());

			if (response.success) {
				// v3/webpay/gateway returns payment methods in data.data
				const paymentMethodsData = response.data?.data || [];

				setPaymentMethods(paymentMethodsData);
				return {
					success: true,
					data: paymentMethodsData,
					message: 'Payment methods fetched successfully',
				};
			} else {
				setError(response.message || 'Failed to fetch payment methods');
				return {
					success: false,
					message: response.message || 'Failed to fetch payment methods',
				};
			}
		} catch (err) {
			const errorMessage = err.message || 'Failed to fetch payment methods. Please try again.';
			setError(errorMessage);
			return {
				success: false,
				message: errorMessage,
			};
		} finally {
			setLoading(false);
		}
	}, []);

  /**
   * Clear KESS payment data and error
   */
  const clearPaymentData = useCallback(() => {
    setKessPaymentData(null);
    setError(null);
  }, []);

  return {
    loading,
    error,
    kessPaymentData,
	  paymentMethods,
    queryKessPayment,
    linkExistingPayment,
	  linkManualPayment,
    updatePaymentLink,
	  fetchPaymentMethods,
    clearPaymentData,
    setKessPaymentData,
  };
};

export default useKessPaymentLink;
